import React from 'react';
import ServicesContext from '../contexts/ServicesContext';
import { FileFormat } from '../enums/FileFormat';
import { SeparationType } from '../enums/SeparationType';
import { OrderForListing } from '../models/OrderForListing';
import { strings } from '../resources/strings';
import { GetDanfePackingLabelInput } from '../services/Labels/GetDanfePackingLabel/GetDanfePackingLabelInput';
import { GetLensCertificateLabelInput } from '../services/Labels/GetLensCertificateLabel/GetLensCertificateLabelInput';
import { GetSimplePackingLabelInput } from '../services/Labels/GetSimplePackingLabel/GetSimplePackingLabelInput';
import { GenerateSeparationListReportInput } from '../services/Reports/GenerateSeparationListReport/GenerateSeparationListReportInput';
import { GenerateShippingGuideReportInput } from '../services/Reports/GenerateShippingGuideReport/GenerateShippingGuideReportInput';
import { GenerateTSOReportInput } from '../services/Reports/GenerateTSOReport/GenerateTSOReportInput';
import { TSOBarcodeMode } from '../enums/TSOBarcodeMode';
import { DanfeLabelBarcodeMode } from '../enums/DanfeLabelBarcodeMode';
import { GenerateLensSeparationListReportInput } from '../services/Reports/GenerateLensSeparationListReport/GenerateLensSeparationListReportInput';
import { StatePrescriptionIdentification } from '../enums/StatePrescriptionIdentification';
import { Result } from '../common-models/Result';
import { QueueReportJobOutput } from '../models/QueueReportJobOutput';
import { CallbackResultType } from '../enums/CallbackResultType';

export function usePrintListOrders() {

  const {
    generateSeparationListReportService,
    generateLensSeparationListReportService,
    generateShippingGuideReportService,
    generateTSOReportService,
    getDanfePackingLabelService,
    getSimplePackingLabelService,
    getLensCertificateLabelService,
    toastService,
    alertService
  } = React.useContext(ServicesContext);

  function getSelectedOrderIds(
    ordersForListing: OrderForListing[]
  ) {
    return ordersForListing?.filter(xo => xo._selected)?.map(xo => xo.order.id) || [];
  }

  function getSelectedOrders(
    ordersForListing: OrderForListing[]
  ) {
    return ordersForListing?.filter(xo => xo._selected) || [];
  }

  function printListToSeparate(
    separationType: SeparationType,
    ordersForListing: OrderForListing[],
    callback?: (success: boolean) => void
  ) {
    const input = new GenerateSeparationListReportInput();
    input.orderIds = getSelectedOrderIds(ordersForListing);
    input.title = '';
    if (separationType === SeparationType.Prescription) {
      input.title = `${strings.ListOfSeparation} - ${strings.OrderWithDegree}`;
    } else if (separationType === SeparationType.NonPrescription) {
      input.title = `${strings.ListOfSeparation} - ${strings.OrderNonDegree}`;
    } else if (separationType === SeparationType.HomeTryOn) {
      input.title = `${strings.ListOfSeparation} - ${strings.ZerezesAtHome}`;
    }
    generateSeparationListReportService.handle(input, callback);
  }

  async function printLensSeparationList(fileFormat: FileFormat, ordersForListing: OrderForListing[], callback?: (resultType: CallbackResultType) => void) {
    if (fileFormat != FileFormat.Pdf && fileFormat != FileFormat.Csv) {
      toastService.warning(strings.MessageToFileFormatInvalid);
      callback?.(CallbackResultType.Cancelled);
      return;
    }

    const selectedOrders = getSelectedOrders(ordersForListing);
    const numberOfOrders = selectedOrders.length;
    const numberOfOrdersThatAreWaitingLenses = selectedOrders.filter(order =>
      order.statePrescriptionLog?.currentStateId === StatePrescriptionIdentification.AguardandoLente).length;
    const numberOfOrdersThatAreNotWaitingLenses = numberOfOrders - numberOfOrdersThatAreWaitingLenses;
    if (numberOfOrdersThatAreNotWaitingLenses > 0) {
      let message = '';
      if (numberOfOrdersThatAreWaitingLenses === 0) {
        message = `Nenhum pedido selecionado aguardando lente.`;
      } else {
        message = `De ${numberOfOrders} pedidos selecionados, `;
        if (numberOfOrdersThatAreWaitingLenses > 1) {
          message += ` ${numberOfOrdersThatAreWaitingLenses} estão aguardando lente.`;
        } else {
          message += ` um está aguardando lente.`;
        }
      }
      message += `<br>Deseja realmente continuar?`;
      const confirmation = await alertService.confirmHtml(message);
      if (!confirmation.isConfirmed) {
        callback?.(CallbackResultType.Cancelled);
        return;
      }
    }

    const input = new GenerateLensSeparationListReportInput();
    input.orderIds = getSelectedOrderIds(selectedOrders);
    input.title = `Lista de Separação de Lentes`;

    if (fileFormat == FileFormat.Pdf) {
      generateLensSeparationListReportService.handlePdf(input, callback);
      return;
    }

    if (fileFormat == FileFormat.Csv) {
      generateLensSeparationListReportService.handleCsv(input, callback);
      return;
    }
  }

  function printShippingGuide(
    ordersForListing: OrderForListing[],
    version?: number,
    callback?: (success: boolean) => void
  ) {
    const input = new GenerateShippingGuideReportInput();
    input.orderIds = getSelectedOrderIds(ordersForListing);
    if (!input.orderIds || input.orderIds.length <= 0) {
      toastService.warning(strings.MessageToPrintingSelectedOrdersEmpty);
      return;
    }
    generateShippingGuideReportService.handle(input, version, callback);
  }
  function queuePrintShippingGuide(
    ordersForListing: OrderForListing[],
    userId: number,
    version?: number
  ): Promise<Result<QueueReportJobOutput> | null> {
    const input = new GenerateShippingGuideReportInput();
    input.orderIds = getSelectedOrderIds(ordersForListing);
    input.version = version || 0;
    input.userId = userId;
    if (!input.orderIds || input.orderIds.length <= 0) {
      toastService.warning(strings.MessageToPrintingSelectedOrdersEmpty);
      return Promise.resolve(null);
    }
    return generateShippingGuideReportService.queueJob(input);
  }

  function printTSO(
    ordersForListing: OrderForListing[],
    version?: number,
    barcodeMode?: TSOBarcodeMode,
    callback?: (resultType: CallbackResultType) => void
  ) {
    const input = new GenerateTSOReportInput();
    input.orderIds = getSelectedOrderIds(ordersForListing);
    input.barcodeMode = barcodeMode;
    if (!input.orderIds || input.orderIds.length <= 0) {
      toastService.warning(strings.MessageToPrintingSelectedOrdersEmpty);
      callback?.(CallbackResultType.Cancelled);
      return;
    }
    generateTSOReportService.handle(input, version, callback);
  }

  function printZplDanfePackingLabel(
    ordersForListing: OrderForListing[],
    callback?: (resultType: CallbackResultType) => void,
    barcodeMode?: DanfeLabelBarcodeMode
  ) {
    printDanfePackingLabel(ordersForListing, FileFormat.Zpl, callback, barcodeMode);
  }

  function printZipZplDanfePackingLabel(
    ordersForListing: OrderForListing[],
    callback?: (resultType: CallbackResultType) => void,
    barcodeMode?: DanfeLabelBarcodeMode
  ) {
    printDanfePackingLabel(ordersForListing, FileFormat.ZipZpl, callback, barcodeMode);
  }

  function printPdfDanfePackingLabel(ordersForListing: OrderForListing[], callback?: (resultType: CallbackResultType) => void) {
    printDanfePackingLabel(ordersForListing, FileFormat.Pdf, callback);
  }

  async function printDanfePackingLabel(
    ordersForListing: OrderForListing[],
    fileFormat: FileFormat,
    callback?: (resultType: CallbackResultType) => void,
    barcodeMode?: DanfeLabelBarcodeMode
  ) {
    const fileFormatIsValid =
      fileFormat === FileFormat.Zpl
      || fileFormat === FileFormat.ZipZpl
      || fileFormat === FileFormat.Pdf;

    if (!fileFormatIsValid) {
      toastService.warning(strings.MessageToFileFormatInvalid);
      callback?.(CallbackResultType.Cancelled);
      return;
    }

    const inputWithAllSelectedOrders = new GetDanfePackingLabelInput();
    inputWithAllSelectedOrders.orderIds = getSelectedOrderIds(ordersForListing);
    if (!inputWithAllSelectedOrders.orderIds || inputWithAllSelectedOrders.orderIds.length <= 0) {
      toastService.warning(strings.MessageToPrintingSelectedOrdersEmpty);
      callback?.(CallbackResultType.Cancelled);
      return;
    }

    const result = await getDanfePackingLabelService.dataHandle(inputWithAllSelectedOrders);
    const list = result?.value || [];

    const invalidDanfeLabels = list.filter(xd => !xd.hasEnoughDataToGenerateDanfe);
    if (invalidDanfeLabels.length > 0) {
      const validDanfeLabels = list.filter(xd => xd.alerts.length === 0 && xd.hasEnoughDataToGenerateDanfe);
      if (validDanfeLabels.length > 0) {
        const firstMessage = validDanfeLabels.length > 1
          ? `Apenas ${validDanfeLabels.length} etiquetas de ${list.length} podem ser geradas no formato danfe. Deseja continuar?`
          : `Apenas uma etiqueta de ${list.length} pode ser gerada no formato danfe. Deseja continuar?`;
        const firstConfirmation = await alertService.confirm(firstMessage);
        if (firstConfirmation.isConfirmed) {
          const inputValidLabels = new GetDanfePackingLabelInput();
          inputValidLabels.orderIds = validDanfeLabels.map(xvl => xvl.orderId);
          const secondMessage = invalidDanfeLabels.length > 1
            ? `Ainda podemos gerar ${invalidDanfeLabels.length} etiquetas de ${list.length} no formato simples. Deseja continuar?`
            : `Ainda podemos gerar uma etiqueta de ${list.length} no formato simples. Deseja continuar?`;
          const secondConfirmation = await alertService.confirm(secondMessage);
          if (secondConfirmation.isConfirmed) {
            const inputInvalidLabels = new GetDanfePackingLabelInput();
            inputInvalidLabels.orderIds = invalidDanfeLabels.map(xvl => xvl.orderId);
            if (fileFormat === FileFormat.Pdf)
              getSimplePackingLabelService.pdfHandle(inputInvalidLabels);
            else
              getSimplePackingLabelService.fileHandle(inputInvalidLabels);
          }
          handleDanfePackingLabelOutput(inputValidLabels, fileFormat, callback, barcodeMode);
        } else {
          callback?.(CallbackResultType.Cancelled);
        }
      } else {
        const message = `Nenhuma etiqueta pode ser gerada no formato danfe. Deseja gerar as etiquetas no formato simples?`;
        const confirmation = await alertService.confirm(message);
        if (confirmation.isConfirmed) {
          if (fileFormat === FileFormat.Pdf)
            getSimplePackingLabelService.pdfHandle(inputWithAllSelectedOrders, callback);
          else
            getSimplePackingLabelService.fileHandle(inputWithAllSelectedOrders, callback);
        } else {
          callback?.(CallbackResultType.Cancelled);
        }
      }
      return;
    }

    const invalidLabels = list.filter(xd => xd.alerts.length > 0);
    if (invalidLabels.length > 0) {
      const validLabels = list.filter(xd => xd.alerts.length === 0);
      if (validLabels.length > 0) {
        const firstMessage = validLabels.length > 1
          ? `Apenas ${validLabels.length} etiquetas de ${list.length} podem ser geradas. Deseja continuar?`
          : `Apenas uma etiqueta de ${list.length} pode ser gerada. Deseja continuar?`;
        const firstConfirmation = await alertService.confirm(firstMessage);
        if (firstConfirmation.isConfirmed) {
          const inputValidLabels = new GetDanfePackingLabelInput();
          inputValidLabels.orderIds = validLabels.map(xvl => xvl.orderId);
          handleDanfePackingLabelOutput(inputValidLabels, fileFormat, callback, barcodeMode);
        }
      } else {
        const message = `Nenhuma etiqueta pode ser gerada!`;
        await alertService.warning(message);
        callback?.(CallbackResultType.Cancelled);
      }
      return;
    }

    handleDanfePackingLabelOutput(inputWithAllSelectedOrders, fileFormat, callback, barcodeMode);
  }

  function handleDanfePackingLabelOutput(
    input: GetDanfePackingLabelInput,
    fileFormat: FileFormat,
    callback?: (resultType: CallbackResultType) => void,
    barcodeMode?: DanfeLabelBarcodeMode
  ) {
    if (fileFormat === FileFormat.Pdf) {
      getDanfePackingLabelService.pdfHandle(input, callback);
      return;
    }

    input.barcodeMode = barcodeMode;

    if (fileFormat === FileFormat.ZipZpl) {
      getDanfePackingLabelService.zipFileHandle(input, callback);
      return;
    }

    getDanfePackingLabelService.fileHandle(input, callback);
  }

  function printZplSimplePackingLabel(
    ordersForListing: OrderForListing[],
    callback?: (resultType: CallbackResultType) => void
  ) {
    const input = new GetSimplePackingLabelInput();
    input.orderIds = getSelectedOrderIds(ordersForListing);
    if (!input.orderIds || input.orderIds.length <= 0) {
      toastService.warning(strings.MessageToPrintingSelectedOrdersEmpty);
      callback?.(CallbackResultType.Cancelled);
      return;
    }
    getSimplePackingLabelService.fileHandle(input, callback);
  }

  function printPdfSimplePackingLabel(
    ordersForListing: OrderForListing[],
    callback?: (resultType: CallbackResultType) => void
  ) {
    const input = new GetSimplePackingLabelInput();
    input.orderIds = getSelectedOrderIds(ordersForListing);
    if (!input.orderIds || input.orderIds.length <= 0) {
      toastService.warning(strings.MessageToPrintingSelectedOrdersEmpty);
      callback?.(CallbackResultType.Cancelled);
      return;
    }
    getSimplePackingLabelService.pdfHandle(input, callback);
  }

  function printZplLensCertificateLabel(
    ordersForListing: OrderForListing[],
    callback?: (resultType: CallbackResultType) => void
  ) {
    const input = new GetLensCertificateLabelInput();
    input.orderIds = getSelectedOrderIds(ordersForListing);
    if (!input.orderIds || input.orderIds.length <= 0) {
      toastService.warning(strings.MessageToPrintingSelectedOrdersEmpty);
      callback?.(CallbackResultType.Cancelled);
      return;
    }
    getLensCertificateLabelService.fileHandle(input, callback);
  }

  function printPdfLensCertificateLabel(
    ordersForListing: OrderForListing[],
    callback?: (resultType: CallbackResultType) => void
  ) {
    const input = new GetLensCertificateLabelInput();
    input.orderIds = getSelectedOrderIds(ordersForListing);
    if (!input.orderIds || input.orderIds.length <= 0) {
      toastService.warning(strings.MessageToPrintingSelectedOrdersEmpty);
      callback?.(CallbackResultType.Cancelled);
      return;
    }
    getLensCertificateLabelService.pdfHandle(input, callback);
  }

  return {
    printListToSeparate,
    printLensSeparationList,
    printShippingGuide,
    queuePrintShippingGuide,
    printTSO,
    printZplDanfePackingLabel,
    printZipZplDanfePackingLabel,
    printPdfDanfePackingLabel,
    printZplSimplePackingLabel,
    printPdfSimplePackingLabel,
    printZplLensCertificateLabel,
    printPdfLensCertificateLabel
  };
}
